import * as SelectPrimitive from '@radix-ui/react-select';
import cx from 'classnames';
import React, { forwardRef, useContext } from 'react';

import { type Weight } from '../../theme/types';

import Icon from '../../Icon';
import Text from '../../Text';

import styles from './SelectButton.module.css';

import { ReactComponent as ChevronDownSvg } from '@frontend/icons/chevron-down.svg';
import { SelectContext } from '../Select';

interface SelectValueProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value> {
  weight?: Extract<Weight, '400' | '600'>;
  customValue?: React.ReactNode;
}

export const SelectValue = ({
  weight,
  customValue,
  ...otherProps
}: SelectValueProps) => {
  return (
    <Text
      as="div"
      color="neutral-900"
      className={styles['selected-option']}
      weight={weight}
    >
      {customValue || <SelectPrimitive.Value {...otherProps} />}
    </Text>
  );
};

const SelectButton = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    label?: string;
  }
>(({ className, label, children, ...otherProps }, ref): JSX.Element => {
  const { value } = useContext(SelectContext);

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cx(
        styles['base'],
        {
          [styles['has-value']]: !!value,
        },
        className
      )}
      {...otherProps}
    >
      <div
        className={cx(styles['main-wrapper'], {
          [styles['main-wrapper--no-label']]: !label,
        })}
      >
        {label && <div className={styles['label']}>{label}</div>}
        {children}
      </div>
      <div className={styles['indicator']}>
        <SelectPrimitive.Icon asChild>
          <Icon fillWithCurrentColor={false} component={<ChevronDownSvg />} />
        </SelectPrimitive.Icon>
      </div>
    </SelectPrimitive.Trigger>
  );
});

export default SelectButton;
